import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { Col, Input, Modal, Row, Select, Spin, Table } from "antd";
import { useFormik } from "formik";
import { get, map } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { default as Button, default as PFButton } from "../../../components/functional_components/Button/PFButton";
import { setLoading } from "../../../redux/ducks/Loading";
import PfpService from "../../../services/api/PfpService";
import Wbs from "../../../services/api/Wbs";
import WbsCategory from "../../../services/api/WbsCategory";
import { paginationDefaults } from "../../../utils/properties";
import { showError, showSuccess, trimData } from "../../../utils/utilities";
const { Option } = Select;

const WbsStructure = () => {
  const { confirm } = Modal;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [state, setState] = useState({
    tableContent: [],
    categoriesOptionsList: [],
    servicesOptionsList: [],
    selectedWbs: {},
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    loading: false,
    modal: {
      visible: false,
      type: "",
      data: {},
    },
  });

  useEffect(() => {
    getWbsStructure();
    getCategory();
    getServices();
  }, []);

  const formikWbsStructure = useFormik({
    initialValues: {
      id: get(state.selectedWbs, "id", ""),
      name: get(state.selectedWbs, "name", ""),
      category: get(state.selectedWbs, "category.id", null),
      services: map(get(state.selectedWbs, "serviceList", []), "id"),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.id) {
        errors.id = "Required!";
      }
      if (!values.name) {
        errors.name = "Required!";
      }
      if (!values.category) {
        errors.category = "Required!";
      }
      if (!values.services) {
        errors.services = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      submitWbs(values);
    },
  });

  const submitWbs = async (values) => {
    setState((prevState) => ({ ...prevState, loading: true }));
    const serviceList = [];
    values.services.forEach((serviceId) => serviceList.push({ id: serviceId }));
    const request = {
      category: {
        id: values.category,
      },
      id: values.id,
      name: values.name,
      serviceList,
    };
    const response = await Wbs.saveWbsStructure(request);
    if (!response.error) {
      showSuccess();
      getWbsStructure();
      closeModal();
    } else {
      showError(get(response, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const getWbsStructure = async (page = state.pagination.currentPage) => {
    dispatch(setLoading(true));
    const wbsStructureList = await Wbs.getWbsStructureList(`page=${page - 1}&size=${state.pagination.size}`);
    if (!wbsStructureList.error && wbsStructureList.data) {
      wbsStructureList.data.forEach((service, i) => {
        service.key = i; // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: get(wbsStructureList, "data", []),
        pagination: {
          currentPage: wbsStructureList.currentPage ? wbsStructureList.currentPage + 1 : paginationDefaults.page,
          size: get(wbsStructureList, "pageSize", paginationDefaults.size),
          totalItems: get(wbsStructureList, "totalItems", paginationDefaults.totalItems),
        },
        loading: false,
      }));
    } else {
      showError(get(wbsStructureList, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const columns = [
    { title: t("wbs.table.id"), dataIndex: "id", key: "id" },
    { title: t("wbs.table.name"), dataIndex: "name", key: "name" },
    {
      title: t("wbs.table.category"),
      responsive: ["md"],
      dataIndex: "category",
      key: "category",
      render: (value) => {
        return `${value.id} | ${value.name}`;
      },
    },
    {
      title: t("general.actions"),
      dataIndex: "",
      key: "x",
      width: 80,
      render: (record) => {
        return (
          <div className="space-around">
            <div onClick={() => editCallback(record.id)}>
              <EditOutlined className="icon-medium" />
            </div>
            {!record.deletable ? (
              <Col>
                {" "}
                <DeleteOutlined className="icon-medium disabled no-pointer" />{" "}
              </Col>
            ) : (
              <Col onClick={() => deleteCallback(record.id)}>
                {" "}
                <DeleteOutlined className="icon-medium" />
              </Col>
            )}
          </div>
        );
      },
    },
  ];

  const editCallback = async (id) => {
    setState((prevState) => ({ ...prevState, loading: true, modal: { visible: true, type: "edit" } }));
    const currentWbsStructure = await Wbs.wbsStructureData(id);
    if (!currentWbsStructure.error && currentWbsStructure.data) {
      setState((prevState) => ({ ...prevState, selectedWbs: currentWbsStructure.data }));
    } else {
      showError(get(currentWbsStructure, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const deleteCallback = async (id) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        dispatch(setLoading(true));
        const deleteResponse = await Wbs.deleteWbsStructure(id);
        if (!deleteResponse.error) {
          showSuccess();
          getWbsStructure();
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
        dispatch(setLoading(false));
      },
      onCancel() {},
    });
  };

  const closeModal = () => {
    if (state.modal.type === "new") {
      formikWbsStructure.resetForm();
    }
    setState((prevState) => ({ ...prevState, selectedWbs: {}, selectedServices: [] }));
    setState((prevState) => ({ ...prevState, modal: { visible: false, type: state.modal.type } }));
  };

  const changeCategory = (value) => {
    formikWbsStructure.setFieldValue("category", value);
    if (value === undefined) {
      setState((prevState) => ({ ...prevState, categoriesOptionsList: [] }));
    }
  };

  const getCategory = async () => {
    const categories = await WbsCategory.getWbsCategoriesList(`allPages=true`);
    if (categories && categories.data) {
      setState((prevState) => ({
        ...prevState,
        categoriesOptionsList: categories.data,
      }));
    }
  };

  const changeServices = (value) => {
    formikWbsStructure.setFieldValue("services", value);
  };

  const getServices = async () => {
    const services = await PfpService.getServicesList(`allPages=true`);
    if (services && services.data) {
      setState((prevState) => ({
        ...prevState,
        servicesOptionsList: services.data,
      }));
    }
  };

  return (
    <>
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={() => setState((prevState) => ({ ...prevState, modal: { visible: true, type: "new" } }))}
        />
      </Row>
      <Table
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                size: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            getWbsStructure(page);
          },
        }}
        columns={columns}
        dataSource={state.tableContent}
        size="small"
      />

      <Modal
        width={700}
        title={state.modal.type === "edit" ? state.selectedWbs.id : t("wbs.newWbsStructure")}
        open={state.modal.visible}
        onCancel={closeModal}
        footer={[
          <PFButton
            key="okButton"
            isDisabled={!(formikWbsStructure.dirty && formikWbsStructure.isValid)}
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikWbsStructure.submitForm}
          />,
        ]}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <>
          <Row gutter={[40, 24]}>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold mt-10" htmlFor="id">
                  {t("wbs.table.id")}*
                </label>
              </Row>
              <Row>
                <Input
                  required
                  id="id"
                  name="id"
                  onChange={formikWbsStructure.handleChange}
                  onBlur={formikWbsStructure.handleBlur}
                  value={formikWbsStructure.values.id}
                  disabled={state.modal.type === "edit"}
                />
              </Row>
            </Col>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold mt-10" htmlFor="name">
                  {t("wbs.table.name")}*
                </label>
              </Row>
              <Row>
                <Input
                  required
                  id="name"
                  name="name"
                  onChange={formikWbsStructure.handleChange}
                  onBlur={formikWbsStructure.handleBlur}
                  value={formikWbsStructure.values.name}
                />
              </Row>
            </Col>
          </Row>
          <Row gutter={[40, 48]}>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold mb-10 mt-10" htmlFor="category">
                  {t("wbs.table.category")}*
                </label>
              </Row>
              <Row>
                <Select
                  allowClear
                  className="w-100"
                  id="category"
                  name="category"
                  showSearch
                  optionFilterProp="children"
                  onChange={(value) => {
                    changeCategory(value);
                  }}
                  placeholder={t("wbs.table.category")}
                  onBlur={formikWbsStructure.handleBlur}
                  value={formikWbsStructure.values.category}
                >
                  {state.categoriesOptionsList.length > 0 &&
                    state.categoriesOptionsList.map((data) => {
                      return (
                        <Option key={data.id} value={data.id} name={data.name}>
                          {data.id}
                        </Option>
                      );
                    })}
                </Select>
              </Row>
            </Col>
            <Col lg={12} md={12} xs={24} sm={12}>
              <Row>
                <label className="fw-bold mb-10 mt-10" htmlFor="services">
                  {t("wbs.table.services")}
                </label>
              </Row>
              <Row>
                <Select
                  showSearch
                  className="w-100"
                  id="services"
                  name="services"
                  placeholder={t("wbs.table.services")}
                  mode="multiple"
                  value={formikWbsStructure.values.services}
                  onBlur={formikWbsStructure.handleBlur}
                  onChange={(value) => {
                    changeServices(value);
                  }}
                >
                  {state.servicesOptionsList.length > 0 &&
                    state.servicesOptionsList.map((data) => {
                      return (
                        <Option key={data.id} value={data.id} name={data.name}>
                          {data.id}
                        </Option>
                      );
                    })}
                </Select>
              </Row>
            </Col>
          </Row>
        </>
      </Modal>
    </>
  );
};

export default WbsStructure;
